import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import {
  isAvatarKey,
  parentReportSchema,
  whenItWas,
  type ChildReport,
  type ParentReport,
  type SkillReport,
} from '@hazel/shared'

import { api } from '../api/client.js'
import { Avatar } from '../components/Avatar.js'
import { Card } from '../components/Card.js'

/** How a skill is going, said in words rather than as a badge. */
const MOVEMENT: Readonly<Record<SkillReport['movement'], string>> = {
  moving: 'going well',
  stuck: 'hard going',
  resting: 'not lately',
  new: 'just started',
}

const TONE: Readonly<Record<SkillReport['movement'], string>> = {
  moving: 'text-teal',
  stuck: 'text-coral',
  resting: 'text-amber',
  new: 'text-ink-muted',
}

/** "about half the time", which is more use to a parent than "61%". */
function howOften(wrong: number, asked: number): string {
  const share = wrong / asked

  if (share >= 0.8) return 'nearly every time'
  if (share >= 0.55) return 'more often than not'
  if (share >= 0.45) return 'about half the time'
  if (share >= 0.25) return 'now and then'
  return 'occasionally'
}

function Child({ child }: { child: ChildReport }) {
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        {/* Narrowed rather than cast: the report carries whatever string is
            in the database, and a key this build does not know should fall
            back to an initial rather than being forced through the types. */}
        <Avatar
          teddy={child.avatarKey && isAvatarKey(child.avatarKey) ? child.avatarKey : null}
          name={child.displayName}
          size="medium"
        />
        <div>
          <h2 className="text-(length:--text-heading)">{child.displayName}</h2>
          <p className="text-ink-muted text-(length:--text-small)">
            {`${child.soloRounds} rounds on her own`}
            {child.roomRounds > 0 ? `, ${child.roomRounds} in a room` : ''}
          </p>
        </div>
      </div>

      {!child.enough ? (
        // Honest about thin data. A confident chart drawn from four rounds is
        // worse than saying there is not enough yet.
        <p className="text-ink-muted">
          Not enough yet to say much. A few more rounds of maths and this fills in.
        </p>
      ) : (
        <>
          <section className="flex flex-col gap-2">
            <h3 className="font-display text-(length:--text-small)">How the skills are going</h3>
            <ul className="flex list-none flex-col gap-1 p-0">
              {child.skills.map((skill) => (
                <li key={skill.skillKey} className="flex flex-wrap items-baseline gap-x-3">
                  <span className="flex-1">{skill.label}</span>
                  <span className={`text-(length:--text-small) ${TONE[skill.movement]}`}>
                    {MOVEMENT[skill.movement]}
                  </span>
                  <span className="text-ink-muted text-(length:--text-small) tabular-nums">
                    {`${skill.correct}/${skill.attempts} right · ${skill.fluent} straight off`}
                  </span>
                  <span className="text-ink-muted text-(length:--text-small)">
                    {skill.lastSeenAt ? whenItWas(skill.lastSeenAt) : ''}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {child.trouble.length > 0 ? (
            <section className="flex flex-col gap-2">
              <h3 className="font-display text-(length:--text-small)">
                Questions worth a car journey
              </h3>
              <ul className="flex list-none flex-col gap-1 p-0">
                {child.trouble.map((spot) => (
                  <li key={spot.prompt}>
                    <span className="font-display">{spot.prompt}</span>{' '}
                    <span className="text-ink-muted">
                      {`comes up wrong ${howOften(spot.wrong, spot.asked)}`}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {child.reading.length > 0 ? (
            <section className="flex flex-col gap-2">
              <h3 className="font-display text-(length:--text-small)">Reading</h3>
              <p className="text-ink-muted">
                {child.reading.map((band) => `${band.rounds} at ${band.band}`).join(', ')}
              </p>
            </section>
          ) : null}
        </>
      )}
    </Card>
  )
}

/**
 * How she is getting on, for a parent.
 *
 * Never a comparison with another child and never an age. There is one child
 * using this app and the question is whether she is getting better than she
 * was.
 */
export function ParentView() {
  const [report, setReport] = useState<ParentReport | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    void api<unknown>('/api/parent/report')
      .then((body) => {
        const parsed = parentReportSchema.safeParse(body)
        if (parsed.success) setReport(parsed.data)
        else setFailed(true)
      })
      .catch(() => setFailed(true))
  }, [])

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t load that just now. Have another go in a moment.
      </p>
    )
  }

  if (!report) {
    return (
      <p role="status" className="py-16 text-center">
        Reading her answers…
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-(length:--text-hero)">How she’s getting on</h1>

      {report.children.length === 0 ? (
        <Card>
          <p>
            No accounts yet. <Link to="/accounts">Add one</Link> and this fills in once she starts
            playing.
          </p>
        </Card>
      ) : (
        report.children.map((child) => <Child key={child.userId} child={child} />)
      )}

      {/*
        What these numbers rest on, said where a parent reads them.
        Solo rounds are reported by the browser she plays in: it is the one she
        uses, and nobody is trying to fool it — but "fluent" means the times it
        sent, and the wrong answers here are the ones it mentioned. See DOL-30.
      */}
      <p className="text-ink-muted text-(length:--text-small)">
        These come from what her device reported as she played. A round played in a room is counted
        separately: everybody in a race gets the same questions, so it says nothing about where her
        own level is.
      </p>
    </div>
  )
}
